import { kyRequest } from './kyHttp';

const BASE_URL = 'assessment';

// JSON Schema untuk response pertanyaan dari AI
const questionSchema = {
  assessmentId: 'string', // Unique ID untuk session assessment ini
  userId: 'string', // Optional, jika user sudah login
  generatedAt: 'ISO 8601 timestamp',
  expiresAt: 'ISO 8601 timestamp', // Optional, jika ada batas waktu
  categories: [
    {
      id: 'string', // e.g., "problems_challenges"
      title: 'string', // e.g., "Masalah & Tantangan"
      description: 'string', // Deskripsi kategori
      order: 'number', // Urutan tampil
      questions: [
        {
          id: 'string', // Unique question ID
          text: 'string', // Pertanyaan
          type: 'checkbox', // Untuk konsistensi dengan UI yang ada
          required: false,
          metadata: {
            weight: 'number', // Bobot untuk scoring
            tags: ['string'], // Tags untuk mapping ke produk
            aiContext: 'string', // Context untuk AI processing
          },
        },
      ],
      essay: {
        id: 'string',
        prompt: 'string', // e.g., "Ada tantangan lain yang ingin Anda sampaikan?"
        maxWords: 150,
        required: false,
        metadata: {
          aiContext: 'string',
        },
      },
    },
  ],
  metadata: {
    businessType: 'string', // Jenis bisnis user (jika diketahui)
    businessStage: 'string', // Tahap bisnis (startup, growth, etc)
    previousAssessments: 'number', // Jumlah assessment sebelumnya
    aiModel: 'string', // Model AI yang digunakan
    personalizationFactors: ['string'], // Faktor yang digunakan untuk personalisasi
  },
};

// JSON Schema untuk submit jawaban
const answerSubmissionSchema = {
  assessmentId: 'string', // ID dari assessment yang dijawab
  userId: 'string', // Optional
  completedAt: 'ISO 8601 timestamp',
  answers: {
    category_id: {
      checkboxes: ['question_id'], // Array of selected question IDs
      essay: 'string', // Essay answer if any
      showEssay: 'boolean', // Whether essay was shown
    },
  },
  metadata: {
    timeSpent: 'number', // Waktu dalam seconds
    deviceInfo: 'object', // Optional device/browser info
    completionRate: 'number', // Persentase completion
  },
};

// JSON Schema untuk response rekomendasi
const recommendationSchema = {
  assessmentId: 'string',
  userId: 'string',
  generatedAt: 'ISO 8601 timestamp',
  analysis: {
    summary: 'string', // Ringkasan hasil assessment
    strengths: ['string'], // Kekuatan bisnis user
    weaknesses: ['string'], // Area yang perlu diperbaiki
    opportunities: ['string'], // Peluang pengembangan
    priority: 'string', // Prioritas utama
  },
  recommendations: [
    {
      type: 'course|consultation|workshop', // Tipe produk
      productId: 'string', // ID produk di database
      title: 'string',
      description: 'string',
      duration: 'string', // e.g., "4 kursus • 48 jam"
      price: 'number',
      originalPrice: 'number', // Harga sebelum diskon
      discount: 'number', // Persentase diskon
      relevanceScore: 'number', // 0-100, seberapa relevan dengan kebutuhan
      reasonsToTake: ['string'], // Alasan kenapa cocok
      learningOutcomes: ['string'], // Apa yang akan dipelajari
      prerequisites: ['string'], // Prasyarat jika ada
      metadata: {
        instructor: 'string',
        rating: 'number',
        totalStudents: 'number',
        category: 'string',
      },
    },
  ],
  bundle: {
    available: 'boolean',
    discount: 'number', // Extra discount jika beli bundle
    totalPrice: 'number',
    originalPrice: 'number',
    savings: 'number',
    validUntil: 'ISO 8601 timestamp',
  },
  nextSteps: {
    immediate: ['string'], // Langkah yang bisa diambil sekarang
    shortTerm: ['string'], // 1-3 bulan
    longTerm: ['string'], // 3-12 bulan
  },
  personal_learning_path: {
    id: 'string', // ID personal learning path
    title: 'string', // Judul personal learning path
    slug: 'string', // URL slug
    description: 'string', // Deskripsi program
    pricing: {
      original_price: 'number',
      remaining_amount: 'number',
      discount: 'number',
    },
    duration: 'string', // Durasi program
    features: ['string'], // Fitur yang disediakan
    is_available: 'boolean',
  },
};

/**
 * Generate personalized assessment questions
 * @param {Object} userProfile - User profile data for personalization
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     assessmentId: "9c5cce41-68b3-4fee-9237-553d52219b78",
 * //     categories: [...]
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const generateAssessmentQuestions = (userProfile = {}) => {
  return kyRequest
    .post(
      `${BASE_URL}/generate-questions`,
      {
        userProfile,
        language: 'id', // Bahasa Indonesia
        assessmentType: 'business_needs',
        personalizationLevel: 'high',
      },
      {
        timeout: 60000, // 60 seconds for AI generation
        retry: {
          limit: 1, // Only retry once on failure
          methods: ['post'],
        },
      }
    )
    .json()
    .then((res) => {
      // Handle response based on actual API structure
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error generating assessment questions:', error);

      // Specific handling for timeout errors
      if (error.name === 'TimeoutError') {
        console.warn('AI generation timed out, using default questions');
        return {
          payload: getDefaultQuestions(),
          error: 'timeout',
          success: false,
          message: 'AI generation took too long, using standard questions',
        };
      }

      // Network or server errors
      return {
        payload: getDefaultQuestions(),
        error: error.response || error.message || 'unknown_error',
        success: false,
        message: 'Failed to generate personalized questions, using standard questions',
      };
    });
};

/**
 * Submit assessment answers and get recommendations
 * @param {string} assessmentId - Assessment ID
 * @param {Object} answers - User answers
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} - Response with payload, error, and success status
 */
const submitAssessmentAnswers = (assessmentId, answers, metadata = {}) => {
  return kyRequest
    .post(
      `${BASE_URL}/submit`,
      {
        assessmentId,
        answers,
        metadata,
        requestRecommendations: true,
      },
      {
        timeout: 45000, // 45 seconds for recommendation generation
        retry: {
          limit: 1,
          methods: ['post'],
        },
      }
    )
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error submitting assessment:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Get assessment status
 * @param {string} assessmentId - Assessment ID
 * @returns {Promise<Object>} - Response with payload, error, and success status
 */
const getAssessmentStatus = (assessmentId) => {
  return kyRequest
    .get(`${BASE_URL}/${assessmentId}/status`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error getting assessment status:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Get recommendations for completed assessment
 * @param {string} assessmentId - Assessment ID
 * @returns {Promise<Object>} - Response with payload, error, and success status
 */
const getAssessmentRecommendations = (assessmentId) => {
  return kyRequest
    .get(`${BASE_URL}/${assessmentId}/recommendations`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error getting recommendations:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Save assessment progress (for auto-save)
 * @param {string} assessmentId - Assessment ID
 * @param {Object} currentAnswers - Current answers
 * @param {number} currentStep - Current step
 * @returns {Promise<Object>} - Response with payload, error, and success status
 */
const saveAssessmentProgress = (assessmentId, currentAnswers, currentStep) => {
  return kyRequest
    .post(`${BASE_URL}/save-progress`, {
      assessmentId,
      answers: currentAnswers,
      currentStep,
      savedAt: new Date().toISOString(),
    })
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error saving progress:', error);
      // Fail silently for auto-save
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Load saved assessment
 * @param {string} assessmentId - Assessment ID
 * @returns {Promise<Object>} - Response with payload, error, and success status
 */
const loadAssessment = (assessmentId) => {
  return kyRequest
    .get(`${BASE_URL}/${assessmentId}`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error loading assessment:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Get available AI models
 * @returns {Promise<Object>} - Response with available models
 */
const getAvailableModels = () => {
  return kyRequest
    .get(`${BASE_URL}/models`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { data: payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error getting available models:', error);
      return { data: null, error: error.response, success: false };
    });
};

/**
 * Get latest assessment for current month
 * @returns {Promise<Object>} - Response with latest assessment data
 */
const getLatestAssessment = () => {
  return kyRequest
    .get(`${BASE_URL}/latest`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error getting latest assessment:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Check monthly assessment limit
 * @returns {Promise<Object>} - Response with limit information
 */
const checkAssessmentLimit = () => {
  return kyRequest
    .get(`${BASE_URL}/check-limit`)
    .json()
    .then((res) => {
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('Error checking assessment limit:', error);
      return { payload: null, error: error.response, success: false };
    });
};

/**
 * Submit feedback for assessment results
 * @param {string} assessmentId - Assessment ID
 * @param {number} rating - Rating (1-5)
 * @param {string} feedbackText - Feedback text
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} - Response with success status
 */
const submitAssessmentFeedback = (assessmentId, rating, feedbackText = '', metadata = {}) => {
  const payload = {
    assessment_id: assessmentId,
    rating,
    feedback_text: feedbackText,
    metadata: {
      page_source: 'assessment_result',
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      session_duration: metadata.session_duration || 0,
      ...metadata,
    },
  };

  return kyRequest
    .post(`${BASE_URL}/feedback`, payload)
    .json()
    .then((res) => {
      return {
        payload: res.data || res,
        error: null,
        success: res.success || true,
      };
    })
    .catch((error) => {
      console.error('Error submitting assessment feedback:', error);
      let errorData = null;

      if (error.response) {
        try {
          errorData = error.response;
        } catch (e) {
          errorData = { code: 'NETWORK_ERROR', message: 'Network error occurred' };
        }
      }

      return {
        payload: null,
        error: errorData,
        success: false,
      };
    });
};

/**
 * Get all assessment history for the user with filtering support
 * @param {Object} filters - Filter parameters
 * @param {string} filters.date_start - Start date (YYYY-MM-DD format)
 * @param {string} filters.date_end - End date (YYYY-MM-DD format)
 * @param {number} filters.price_min - Minimum price
 * @param {number} filters.price_max - Maximum price
 * @param {string} filters.search - Search keyword
 * @param {number} filters.page - Page number (default: 1)
 * @param {number} filters.limit - Items per page (default: 10)
 * @returns {Promise<Object>} - Response with assessment history data
 */
const getAllAssessmentHistory = (filters = {}) => {
  // Build query parameters
  const queryParams = new URLSearchParams();

  if (filters.date_start) queryParams.append('date_start', filters.date_start);
  if (filters.date_end) queryParams.append('date_end', filters.date_end);
  if (filters.price_min) queryParams.append('price_min', filters.price_min.toString());
  if (filters.price_max) queryParams.append('price_max', filters.price_max.toString());
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.page) queryParams.append('page', filters.page.toString());
  if (filters.limit) queryParams.append('limit', filters.limit.toString());

  const queryString = queryParams.toString();
  const url = queryString ? `${BASE_URL}/history?${queryString}` : `${BASE_URL}/history`;

  console.log('📡 Fetching assessment history from:', url);
  console.log('📡 Filters applied:', filters);

  return kyRequest
    .get(url)
    .json()
    .then((res) => {
      console.log('✅ Assessment history response:', res);
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('❌ Error fetching assessment history:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error:', error);

      // Handle 404 specifically
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Assessment history endpoint not found (404). Please check if the API route is deployed.',
          error: 'ENDPOINT_NOT_FOUND',
        };
      }

      // Handle different error scenarios
      if (error.response?.data?.message) {
        return {
          success: false,
          message: error.response.data.message,
          error: error.response.data.error || 'Unknown error',
        };
      }

      return {
        success: false,
        message: 'Failed to fetch assessment history',
        error: error.message || 'Network error',
      };
    });
};

// Fallback questions if AI generation fails
const getDefaultQuestions = () => {
  return {
    assessmentId: `default-${Date.now()}`,
    generatedAt: new Date().toISOString(),
    categories: [
      {
        id: 'problems_challenges',
        title: 'Masalah & Tantangan',
        description: 'Identifikasi masalah utama dalam bisnis Anda',
        order: 1,
        questions: [
          {
            id: 'pc1',
            text: 'Apakah bisnis Anda mengalami kesulitan dalam mengelola keuangan?',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['finance', 'management'] },
          },
          {
            id: 'pc2',
            text: 'Apakah Anda kesulitan dalam merekrut dan mempertahankan talenta?',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['hr', 'team'] },
          },
          {
            id: 'pc3',
            text: 'Apakah ada kendala dalam menjangkau target pasar yang tepat?',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['marketing', 'sales'] },
          },
          {
            id: 'pc4',
            text: 'Apakah proses operasional bisnis belum efisien?',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['operations', 'efficiency'] },
          },
        ],
        essay: {
          id: 'pc_essay',
          prompt: 'Tantangan spesifik apa yang sedang Anda hadapi?',
          maxWords: 150,
        },
      },
      {
        id: 'priorities',
        title: 'Prioritas',
        description: 'Apa yang menjadi fokus utama bisnis Anda saat ini?',
        order: 2,
        questions: [
          {
            id: 'pr1',
            text: 'Meningkatkan penjualan dan revenue',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['sales', 'revenue'] },
          },
          {
            id: 'pr2',
            text: 'Membangun tim yang solid',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['team', 'hr'] },
          },
          {
            id: 'pr3',
            text: 'Mengembangkan produk atau layanan baru',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['product', 'innovation'] },
          },
          {
            id: 'pr4',
            text: 'Meningkatkan efisiensi operasional',
            type: 'checkbox',
            metadata: { weight: 1, tags: ['operations', 'efficiency'] },
          },
        ],
        essay: {
          id: 'pr_essay',
          prompt: 'Prioritas lain yang ingin Anda fokuskan?',
          maxWords: 150,
        },
      },
      // Add more default categories as needed
    ],
  };
};

/**
 * Get personal learning path detail by UUID
 * @param {string} uuid - Learning path UUID
 * @returns {Promise<Object>} - Response with detailed assessment data
 */
const getPersonalLearningPathDetail = (uuid) => {
  console.log('📡 Fetching personal learning path detail for UUID:', uuid);

  return kyRequest
    .get(`${BASE_URL}/latest/${uuid}`)
    .json()
    .then((res) => {
      console.log('✅ Personal learning path detail response:', res);
      const payload = res.data || res.payload || res;
      return { payload, error: null, success: true };
    })
    .catch((error) => {
      console.error('❌ Error fetching personal learning path detail:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error:', error);

      // Handle 404 specifically
      if (error.response?.status === 404) {
        return {
          success: false,
          message:
            'Personal learning path not found (404). Please check if the UUID is correct or the API route is deployed.',
          error: 'LEARNING_PATH_NOT_FOUND',
        };
      }

      const errorData = error.response || error.message || error;
      return {
        payload: null,
        message: `Failed to fetch personal learning path detail: ${errorData}`,
        error: errorData,
        success: false,
      };
    });
};

/**
 * Submit assessment with registration for guest users
 * @param {Object} registrationData - User registration data
 * @param {Object} assessmentAnswers - Assessment answers
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} - Response with assessment results or registration status
 */
const submitAssessmentWithRegistration = (registrationData, assessmentAnswers) => {
  const payload = {
    name: registrationData.name,
    email: registrationData.email,
    phone: registrationData.phone,
    password: registrationData.password,
    answers: assessmentAnswers,
  };

  return kyRequest
    .post('assessment/submit-with-registration', payload, {
      timeout: 45000, // 45 seconds for registration + assessment processing
      retry: {
        limit: 1,
        methods: ['post'],
      },
    })
    .json()
    .then((res) => {
      console.log('API Response in aiService:', res);
      
      // If API explicitly returns success: false, treat as error
      if (res.success === false) {
        return { 
          payload: res, 
          data: res,
          error: res.error || res, 
          success: false 
        };
      }
      
      // Otherwise treat as success
      const responseData = res.data || res;
      return { 
        payload: responseData, 
        data: responseData, // For backward compatibility
        error: null, 
        success: true
      };
    })
    .catch(async (error) => {
      console.error('Error submitting assessment with registration:', error);
      
      let errorData = null;
      
      // Handle HTTP errors (like 409 Conflict) that contain JSON response
      if (error.response) {
        try {
          // Try to parse JSON from error response
          const errorResponse = await error.response.json();
          console.log('Parsed error response:', errorResponse);
          
          return { 
            payload: errorResponse, 
            data: errorResponse,
            error: errorResponse.error || errorResponse, 
            success: false 
          };
        } catch (e) {
          console.error('Failed to parse error response JSON:', e);
          errorData = { code: 'NETWORK_ERROR', message: 'Network error occurred' };
        }
      } else {
        errorData = { code: 'NETWORK_ERROR', message: 'Network error occurred' };
      }

      return { 
        payload: null, 
        data: null,
        error: errorData, 
        success: false 
      };
    });
};

export const SERVICE_AI_ASSESSMENT = {
  generateAssessmentQuestions,
  submitAssessmentAnswers,
  getAssessmentStatus,
  getAssessmentRecommendations,
  saveAssessmentProgress,
  loadAssessment,
  getAvailableModels,
  getLatestAssessment,
  checkAssessmentLimit,
  submitAssessmentFeedback,
  getAllAssessmentHistory,
  getPersonalLearningPathDetail,
  submitAssessmentWithRegistration,
};

// Export schemas for documentation
export { questionSchema, answerSubmissionSchema, recommendationSchema };
